/**
 * @Relay no-runtime
 */
/**
 * @graphql cc
 */
import graphql from 'graphql-tag';
import { useMemo } from 'react';
import {
	// eslint-disable-next-line graphql-relay-compat/no-import-apollo-functions -- Read http://go/cfe-no-import-apollo-functions
	useQuery,
} from 'react-apollo';

import { usePageContentId } from '@confluence/page-context';
import { useContentStatus } from '@confluence/page-utils/entry-points/useContentStatus';

import { normalizeContentLastUpdatedData } from '../normalizers/normalizeContentLastUpdatedData';

import type {
	useContentLastUpdatedDetailsPanelContentLastUpdatedQuery as useContentLastUpdatedDetailsPanelContentLastUpdatedQuery$data,
	useContentLastUpdatedDetailsPanelContentLastUpdatedQueryVariables as useContentLastUpdatedDetailsPanelContentLastUpdatedQuery$variables,
} from './__types__/useContentLastUpdatedDetailsPanelContentLastUpdatedQuery';

const useContentLastUpdatedDetailsPanelContentLastUpdatedQuery = graphql`
	query useContentLastUpdatedDetailsPanelContentLastUpdatedQuery(
		$contentId: ID!
		$status: String!
	) {
		content(id: $contentId, status: [$status]) {
			nodes {
				id
				history {
					lastUpdated {
						by {
							type
							displayName
							... on KnownUser {
								accountId
							}
							profilePicture {
								path
							}
						}
					}
				}
			}
		}
	}
`;
type UseContentLastUpdatedProps = {
	skip?: boolean;
};

export const useContentLastUpdated = ({ skip = false }: UseContentLastUpdatedProps = {}) => {
	const [contentId] = usePageContentId();

	const {
		contentStatus,
		loading: isContentStatusLoading,
		error: contentStatusError,
		refetch: refetchContentStatus,
	} = useContentStatus({ skip });

	const { data, loading, error, refetch } = useQuery<
		useContentLastUpdatedDetailsPanelContentLastUpdatedQuery$data,
		useContentLastUpdatedDetailsPanelContentLastUpdatedQuery$variables
	>(useContentLastUpdatedDetailsPanelContentLastUpdatedQuery, {
		variables: {
			contentId: contentId ?? '',
			status: contentStatus ?? '',
		},
		skip: skip || isContentStatusLoading || !!contentStatusError || !contentStatus || !contentId,
	});
	const normalizedData = useMemo(() => normalizeContentLastUpdatedData(data), [data]);

	return {
		data: normalizedData,
		loading,
		error: contentStatusError || error,
		refetch: !!contentStatusError ? refetchContentStatus : refetch,
	};
};
